<template>
	<div class="premium-page">
		<div class="heading">
			<div>
				<h2>Amazon Listing + 高级 A+</h2>
				<p>先提交 Listing，取得 ASIN 后自动创建并提交高级 A+ 审核。</p>
			</div>
			<el-button icon="el-icon-refresh" @click="loadList">刷新任务</el-button>
		</div>

		<el-card shadow="never" class="panel">
			<div slot="header">
				{{ form.id ? `编辑任务 #${form.id}` : "新建上传任务" }}
			</div>
			<el-form label-width="130px" :model="form" size="small">
				<el-form-item label="店铺 / 站点" required>
					<el-select v-model="shopKey" filterable placeholder="选择已授权的 Amazon 站点" style="width: 440px"
						:disabled="!!form.id" @change="fillMarketplace">
						<el-option v-for="shop in shops" :key="`${shop.shopId}:${shop.marketplaceId}`"
							:label="`${shop.shopName} · ${shop.countryName} (${shop.marketplaceId})`"
							:value="`${shop.shopId}:${shop.marketplaceId}`" />
					</el-select>
				</el-form-item>
				<el-form-item label="Seller SKU" required><el-input v-model.trim="form.sellerSku" :disabled="!!form.id"
						maxlength="255" style="width: 440px" /></el-form-item>
				<el-form-item label="Product Type" required><el-input v-model.trim="form.productType"
						placeholder="例如 HOME；按对应站点的 Product Type Definitions 填写" style="width: 440px" /></el-form-item>
				<el-form-item label="A+ 语言" required><el-input v-model.trim="form.locale" placeholder="en-US、de-DE 等"
						style="width: 180px" /></el-form-item>
				<el-form-item label="Listing attributes" required>
					<el-input v-model="listingText" type="textarea" :rows="10" spellcheck="false"
						placeholder="填写 putListingsItem 的 attributes JSON 对象。所需字段由站点和 Product Type 决定。" />
					<div class="hint">
						只填写 attributes 对象；店铺授权、Seller ID、SKU、站点和
						requirements 由后端处理。
					</div>
				</el-form-item>
				<el-form-item label="高级 A+ 文档" required>
					<el-input v-model="aplusText" type="textarea" :rows="13" spellcheck="false"
						placeholder="填写 name 与 contentModuleList，模块类型须为 PREMIUM_*" />
					<div class="hint">
						一份文档最多七个模块。contentType=EBC、locale
						由后端补齐。上传素材后把返回的 ID 填入对应模块。
					</div>
				</el-form-item>
				<el-form-item label="替换现有 A+">
					<el-checkbox v-model="form.allowReplaceExisting">我已确认：若该 ASIN 已有同语言的 A+
						内容，允许这份新文档在审核通过后替换它</el-checkbox>
				</el-form-item>
				<el-form-item>
					<el-button type="primary" :loading="saving" @click="save(false)">保存草稿</el-button>
					<el-button type="success" :loading="saving" @click="save(true)">保存并提交</el-button>
					<el-button @click="resetForm">新建一条</el-button>
				</el-form-item>
			</el-form>
		</el-card>

		<el-card shadow="never" class="panel">
			<div slot="header">A+ 素材上传（当前选中的店铺 / 站点）</div>
			<p class="hint">
				图片上传会返回 uploadDestinationId。视频上传后还需用 Media JSON
				注册视频及缩略图配对，取得 mediaId，再填入视频模块。
			</p>
			<div class="media-row">
				<input ref="assetFile" type="file" accept="image/jpeg,image/png,video/mp4" />
				<el-button size="small" :loading="uploading" @click="uploadAsset">上传素材</el-button>
			</div>
			<el-input v-if="uploadId" :value="uploadId" readonly size="small"><template
					slot="prepend">uploadDestinationId</template></el-input>
			<el-collapse class="media-expand"><el-collapse-item title="注册 Media（视频必需；图片可选）" name="media">
					<el-input v-model="mediaText" type="textarea" :rows="5" spellcheck="false" />
					<el-button size="small" type="primary" :loading="registering" @click="registerMedia">注册
						Media</el-button>
					<el-input v-if="mediaResult" :value="mediaResult" type="textarea" :rows="5" readonly />
				</el-collapse-item></el-collapse>
		</el-card>

		<el-card shadow="never" class="panel">
			<div slot="header">上传任务</div>
			<el-table :data="tasks" v-loading="loading" border size="small" style="width: 100%">
				<el-table-column prop="id" label="ID" width="80" />
				<el-table-column prop="sellerSku" label="Seller SKU" min-width="150" show-overflow-tooltip />
				<el-table-column prop="marketplaceId" label="站点" min-width="130" />
				<el-table-column prop="asin" label="ASIN" min-width="120" />
				<el-table-column label="阶段" min-width="155"><template slot-scope="scope"><el-tag
							:type="tagType(scope.row.stage)" size="small">{{
								stageText(scope.row.stage)
							}}</el-tag></template></el-table-column>
				<el-table-column prop="lastError" label="错误信息" min-width="220" show-overflow-tooltip />
				<el-table-column label="操作" width="220" fixed="right"><template slot-scope="scope">
						<el-button type="text" @click="editTask(scope.row.id)">详情/编辑</el-button>
						<el-button v-if="canSubmit(scope.row.stage)" type="text"
							@click="submitTask(scope.row.id)">提交/重试</el-button>
						<el-button v-if="scope.row.stage === 'CREATE_UNKNOWN'" type="text"
							@click="attachKey(scope.row.id)">补录 Key</el-button>
					</template></el-table-column>
			</el-table>
			<el-pagination class="pager" background layout="total, prev, pager, next" :total="total"
				:current-page.sync="page" :page-size="20" @current-change="loadList" />
		</el-card>
	</div>
</template>

<script>
import {
	premiumShops,
	premiumList,
	premiumDetail,
	premiumSave,
	premiumSubmit,
	premiumAttachKey,
	premiumUploadMedia,
	premiumRegisterMedia,
} from "../api/amazonPremiumListing";

const empty = () => ({
	id: null,
	shopId: null,
	marketplaceId: "",
	sellerSku: "",
	productType: "",
	locale: "en-US",
	allowReplaceExisting: false,
});
const sampleAplus = () =>
	JSON.stringify(
		{
			name: "My Premium A+ Content",
			contentModuleList: [
				{
					contentModuleType: "PREMIUM_IMAGE_TEXT",
					premiumImageText: {
						positionType: "LEFT",
						headline: { value: "Product headline", decoratorSet: [] },
						image: {
							uploadDestinationId: "替换为上传后返回的 ID",
							altText: "Product image",
							imageCropSpecification: {
								size: {
									width: { value: 800, units: "pixels" },
									height: { value: 600, units: "pixels" },
								},
								offset: {
									x: { value: 0, units: "pixels" },
									y: { value: 0, units: "pixels" },
								},
							},
						},
					},
				},
			],
		},
		null,
		2,
	);

export default {
	name: "AmazonPremiumListing",
	data() {
		return {
			shops: [],
			shopKey: "",
			form: empty(),
			listingText: "{}",
			aplusText: sampleAplus(),
			tasks: [],
			total: 0,
			page: 1,
			loading: false,
			saving: false,
			uploading: false,
			registering: false,
			uploadId: "",
			mediaText:
				'{\n  "mediaType": "IMAGE",\n  "uploadDestinationId": "",\n  "title": "Asset title"\n}',
			mediaResult: "",
			refreshTimer: null,
		};
	},
	created() {
		this.loadShops();
		this.loadList();
	},
	mounted() {
	},
	methods: {
		assertOk(res1) {
			const res = res1.data;
			if (!res || res.code !== 200){
				console.log(res);
			}
			return res.data;
		},
		async loadShops() {
			try {
				this.shops = this.assertOk(await premiumShops()) || [];
			} catch (e) {
				this.$message.error(e.message);
			}
		},
		async loadList() {
			this.loading = true;
			try {
				const result = this.assertOk(
					await premiumList({ pageNum: this.page, pageSize: 20 }),
				);
				this.tasks = result.list || [];
				this.total = result.total || 0;
			} catch (e) {
				this.$message.error(e.message);
			} finally {
				this.loading = false;
			}
		},
		fillMarketplace() {
			const selected = this.shops.find(
				(s) => `${s.shopId}:${s.marketplaceId}` === this.shopKey,
			);
			if (selected) {
				this.form.shopId = selected.shopId;
				this.form.marketplaceId = selected.marketplaceId;
			}
		},
		parseJson(value, label) {
			let parsed;
			try {
				parsed = JSON.parse(value);
			} catch (e) {
				throw new Error(`${label} 不是合法 JSON：${e.message}`);
			}
			if (!parsed || Array.isArray(parsed) || typeof parsed !== "object")
				throw new Error(`${label} 必须是 JSON 对象`);
			return parsed;
		},
		async save(andSubmit) {
			this.saving = true;
			try {
				this.fillMarketplace();
				const payload = {
					...this.form,
					listingAttributes: this.parseJson(
						this.listingText,
						"Listing attributes",
					),
					premiumDocument: this.parseJson(this.aplusText, "高级 A+ 文档"),
				};
				const id = this.assertOk(await premiumSave(payload));
				this.form.id = id;
				if (andSubmit) this.assertOk(await premiumSubmit(id));
				this.$message.success(andSubmit ? "已排队提交" : "草稿已保存");
				await this.loadList();
			} catch (e) {
				this.$message.error(e.message);
			} finally {
				this.saving = false;
			}
		},
		async editTask(id) {
			try {
				const task = this.assertOk(await premiumDetail(id));
				this.form = {
					id: task.id,
					shopId: task.shopId,
					marketplaceId: task.marketplaceId,
					sellerSku: task.sellerSku,
					productType: task.productType,
					locale: task.locale,
					allowReplaceExisting: !!task.allowReplaceExisting,
				};
				this.shopKey = `${task.shopId}:${task.marketplaceId}`;
				this.listingText = JSON.stringify(
					JSON.parse(task.listingPayload),
					null,
					2,
				);
				this.aplusText = JSON.stringify(JSON.parse(task.aplusPayload), null, 2);
				window.scrollTo({ top: 0, behavior: "smooth" });
			} catch (e) {
				this.$message.error(e.message);
			}
		},
		resetForm() {
			this.form = empty();
			this.shopKey = "";
			this.listingText = "{}";
			this.aplusText = sampleAplus();
		},
		async submitTask(id) {
			try {
				this.assertOk(await premiumSubmit(id));
				this.$message.success("已排队处理");
				await this.loadList();
			} catch (e) {
				this.$message.error(e.message);
			}
		},
		async attachKey(id) {
			try {
				const { value } = await this.$prompt(
					"先到 Amazon 核对该文档确已创建，再填入 contentReferenceKey",
					"补录内容 Key",
					{ inputPattern: /\S+/, inputErrorMessage: "Key 必填" },
				);
				this.assertOk(await premiumAttachKey(id, value.trim()));
				this.$message.success("已关联内容文档");
				await this.loadList();
			} catch (e) {
				if (e !== "cancel" && e !== "close")
					this.$message.error(e.message || e);
			}
		},
		async uploadAsset() {
			this.fillMarketplace();
			const file = this.$refs.assetFile && this.$refs.assetFile.files[0];
			if (!this.form.shopId || !file)
				return this.$message.warning("请先选择店铺/站点和素材文件");
			this.uploading = true;
			try {
				this.uploadId = this.assertOk(
					await premiumUploadMedia(
						this.form.shopId,
						this.form.marketplaceId,
						file,
					),
				);
				this.mediaText = JSON.stringify(
					{
						mediaType: file.type === "video/mp4" ? "VIDEO" : "IMAGE",
						uploadDestinationId: this.uploadId,
						title: file.name,
					},
					null,
					2,
				);
				this.$message.success("素材已上传到 Amazon");
			} catch (e) {
				this.$message.error(e.message);
			} finally {
				this.uploading = false;
			}
		},
		async registerMedia() {
			this.fillMarketplace();
			if (!this.form.shopId) return this.$message.warning("请先选择店铺/站点");
			this.registering = true;
			try {
				const media = this.parseJson(this.mediaText, "Media");
				const result = this.assertOk(
					await premiumRegisterMedia({
						shopId: this.form.shopId,
						marketplaceId: this.form.marketplaceId,
						media,
					}),
				);
				this.mediaResult = JSON.stringify(result, null, 2);
				this.$message.success("Media 已注册；将 mediaId 填入所需模块");
			} catch (e) {
				this.$message.error(e.message);
			} finally {
				this.registering = false;
			}
		},
		canSubmit(stage) {
			return [
				"DRAFT",
				"ERROR_LISTING",
				"ERROR_ASIN",
				"ERROR_APLUS",
				"REJECTED",
			].includes(stage);
		},
		tagType(stage) {
			if (stage === "DONE") return "success";
			if (
				stage.startsWith("ERROR") ||
				stage === "REJECTED" ||
				stage === "CREATE_UNKNOWN"
			)
				return "danger";
			return "warning";
		},
		stageText(stage) {
			const names = {
				DRAFT: "草稿",
				LISTING_QUEUED: "Listing 待提交",
				WAIT_ASIN: "等待 ASIN",
				APLUS_READY: "A+ 待验证",
				APLUS_CREATING: "A+ 创建中",
				APLUS_LINKING: "关联 ASIN",
				APLUS_SUBMITTING: "提交审核",
				APLUS_REVIEW: "审核中",
				DONE: "已发布",
				ERROR_LISTING: "Listing 失败",
				ERROR_ASIN: "ASIN 查询失败",
				ERROR_APLUS: "A+ 失败",
				REJECTED: "审核拒绝",
				CREATE_UNKNOWN: "创建结果待核对",
			};
			return names[stage] || stage;
		},
	},
};
</script>

<style scoped>
.premium-page {
	max-width: 1250px;
	margin: auto;
}

.heading {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 16px;
}

.heading h2 {
	margin: 0 0 5px;
	font-size: 22px;
}

.heading p,
.hint {
	font-size: 12px;
	color: #909399;
	margin: 4px 0 8px;
}

.panel {
	margin-bottom: 18px;
}

.media-row {
	display: flex;
	gap: 16px;
	align-items: center;
	margin-bottom: 14px;
}

.media-expand {
	margin-top: 10px;
}

.media-expand .el-button {
	margin: 10px 0;
}

.pager {
	margin-top: 16px;
	text-align: right;
}

::v-deep textarea {
	font-family: Consolas, Monaco, monospace;
	line-height: 1.45;
}
</style>
